import React from 'react';
import { Link } from 'react-router-dom';
import cn from '@/utils/cn';
import * as Routes from '@/modules/routes';
import SPAAccountMenu from '../SysadminSPA/shared/SPAAccountMenu';
import SPABrandLink from '../SysadminSPA/shared/SPABrandLink';
import SPARail from '../SysadminSPA/shared/SPARail';
import SPANavSwitcherControl from '../SysadminSPA/shared/SPANavSwitcherControl';
import ProgramTree from '../SysadminSPA/shared/ProgramTree';
import { useProgramSwitcher } from '../SysadminSPA/shared/ProgramTree/usePrograms';
import type { NavLeaf } from '../SysadminSPA/shared/navSections';
import LegacyNavTree from './LegacyNavTree';
import useCollapsedRail from './railCollapse';

// A pick is a full page load into that program's Table of Contents.
const openProgram = (value: string) => window.location.assign(Routes.admin_program_lessons_path(value));

export interface ContentRailProps {
  // When drilled inside a router (admin/ProgramsPage), the crumb back to Programs is a router
  // link to this path; otherwise it is a full load of the programs page.
  backTo?: string;
  // Which sections of the at-rest tree this role can open; see SPAUser.
  canManageContent?: boolean;
  canManageSupport?: boolean;
  // At rest: the page the rail is on, and which tree items are that page's own tabs (router
  // links). Defaults to the browser location and plain anchors.
  currentPath?: string;
  lessonId?: number;
  linkTo?: (item: NavLeaf) => string | null;
  programColor?: string | null;
  // Without a program the rail is at rest: the app tree instead of a program drill.
  programId?: number;
  programTitle?: string;
  sectionId?: number;
  showSearch?: boolean;
  signOutPath: string;
  supportPath: string;
  unitId?: number;
  userName: string;
}

const crumbClass = cn(
  '-tw-mx-1.5 tw-flex tw-min-w-0 tw-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-md tw-px-1.5 tw-py-1',
  'tw-text-sm tw-font-bold tw-text-spa-heading tw-no-underline',
  'hover:tw-bg-spa-bg-tertiary hover:tw-no-underline hover:tw-text-spa-heading',
);

const crumbContent = (
  <>
    <span aria-hidden="true" className="tw-shrink-0 tw-font-normal tw-text-spa-accent-text">‹</span>
    <span className="tw-shrink-0 tw-whitespace-nowrap tw-font-normal tw-text-spa-tertiary">Programs</span>
  </>
);

// The drill header, as SPANav's: the crumb back to Programs, then the program as the switcher.
const ProgramDrillHeader = ({ backTo, programId, title }: { backTo?: string; programId: number; title: string }) => {
  const switcher = useProgramSwitcher(programId, openProgram);

  return (
    <div className="tw-relative tw-flex tw-min-w-0 tw-flex-col">
      {backTo
        ? <Link className={crumbClass} to={backTo}>{crumbContent}</Link>
        : <a className={crumbClass} href={Routes.admin_programs_path()}>{crumbContent}</a>}
      <SPANavSwitcherControl switcher={switcher} title={title} />
    </div>
  );
};

const ContentRail = ({
  backTo,
  canManageContent = true,
  canManageSupport = true,
  currentPath,
  lessonId,
  linkTo,
  programColor = null,
  programId,
  programTitle = '',
  sectionId,
  showSearch = false,
  signOutPath,
  supportPath,
  unitId,
  userName,
}: ContentRailProps) => {
  const [collapsed, setCollapsed] = useCollapsedRail();

  // The site bar already names the program, so the rail's brand row is just the logo.
  const header = <SPABrandLink href={Routes.admin_homes_path()} />;

  return (
    <SPARail
      accentColor={programId ? programColor : null}
      collapsed={collapsed}
      collapseLabel="Collapse content rail"
      expandLabel="Expand content rail"
      footer={<SPAAccountMenu className="tw-w-full" signOutPath={signOutPath} userName={userName} />}
      header={header}
      subheader={programId
        ? <ProgramDrillHeader backTo={backTo} programId={programId} title={programTitle} />
        : undefined}
      onCollapsedChange={setCollapsed}
    >
      {programId ? (
        <ProgramTree
          lessonId={lessonId}
          programId={programId}
          sectionId={sectionId}
          showSearch={showSearch}
          unitId={unitId}
        />
      ) : (
        <LegacyNavTree
          canManageContent={canManageContent}
          canManageSupport={canManageSupport}
          currentPath={currentPath ?? window.location.pathname}
          linkTo={linkTo}
          supportPath={supportPath}
        />
      )}
    </SPARail>
  );
};

export default ContentRail;
